import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { MonitorPlay, Play } from "lucide-react";
import heroVideo from "../../assets/gmw1.mp4";
import { supabase } from "../../lib/supabaseClient";

export default function Hero() {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [heroSubtext, setHeroSubtext] = useState('Play games. Talk real. Then we stream.');
  const [isVideoReady, setIsVideoReady] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase.from('homepage_content').select('*').order('updated_at', { ascending: false }).limit(1);
      if (!error && data?.[0]?.hero_subtext) setHeroSubtext(data[0].hero_subtext);
    };
    load();
  }, []);

  // SCROLL PARALLAX
  const { scrollY } = useScroll();
  const rawContentY = useTransform(scrollY, [0, 500], [0, 150]);
  const rawOpacity = useTransform(scrollY, [0, 400], [1, 0]);
  const contentY = useSpring(rawContentY, { stiffness: 70, damping: 24, mass: 0.7 });
  const opacity = useSpring(rawOpacity, { stiffness: 70, damping: 24, mass: 0.7 });

  // MOUSE MOVEMENT
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const smoothX = useSpring(mouseX, { stiffness: 40, damping: 25 });
  const smoothY = useSpring(mouseY, { stiffness: 40, damping: 25 });

  useEffect(() => {
    if (prefersReducedMotion) return undefined;
    if (typeof window !== 'undefined' && window.innerWidth < 1024) return undefined;
    const handleMouseMove = (e) => {
        const { innerWidth, innerHeight } = window;
        mouseX.set((e.clientX - innerWidth / 2) / 80); 
        mouseY.set((e.clientY - innerHeight / 2) / 80);
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY, prefersReducedMotion]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = () => {
      if (document.hidden) return;
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {
          // Autoplay can still be blocked on some mobile power/data modes.
        });
      }
    };

    const handleCanPlay = () => setIsVideoReady(true);
    const handlePlaying = () => setIsVideoReady(true);
    const handleVisibility = () => {
      if (!document.hidden) {
        setTimeout(tryPlay, 80);
      }
    };

    video.muted = true;
    tryPlay();
    video.addEventListener('loadedmetadata', tryPlay);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('playing', handlePlaying);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', tryPlay);
    window.addEventListener('touchstart', tryPlay, { once: true });

    return () => {
      video.removeEventListener('loadedmetadata', tryPlay);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('playing', handlePlaying);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', tryPlay);
      window.removeEventListener('touchstart', tryPlay);
    };
  }, []);

  const scrollToContent = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  return (
    <section
      id="hero"
      ref={containerRef}
      className="relative h-screen w-full flex flex-col justify-center items-center px-4 sm:px-6 overflow-hidden bg-[#021206]"
    >
      {/* BACKGROUND LAYER */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0">
            <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            disableRemotePlayback
            controls={false}
            disablePictureInPicture
            preload="auto"
            aria-hidden="true"
            className={`w-full h-full object-cover grayscale transition-all duration-700 pointer-events-none hover:grayscale-0 transform-gpu will-change-transform ${isVideoReady ? 'opacity-50 sm:opacity-60' : 'opacity-0'}`}
            >
              <source src={heroVideo} type="video/mp4" />
            </video>
        </div>

        {/* Animated Tech Grid */}
        <motion.div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(#A1FF4F 1px, transparent 1px), linear-gradient(90deg, #A1FF4F 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            x: prefersReducedMotion ? 0 : smoothX,
            y: prefersReducedMotion ? 0 : smoothY,
          }}
        />

        {/* Floating particles removed per request */}

        {/* Vignette & Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#021206] via-[#021206]/40 to-[#021206]/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#021206]/80 via-transparent to-[#021206]/80" />
      </div>

      {/* CONTENT LAYER */}
      <motion.div
        className="relative z-20 w-full max-w-6xl flex flex-col items-center text-center space-y-8 mt-10"
        style={{ y: contentY, opacity }}
      >
        {/* TITLE */}
        <div className="flex flex-col items-center relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
              <h1 className="font-black text-white uppercase italic tracking-tighter leading-[0.85] text-[clamp(3.5rem,11vw,9rem)] drop-shadow-2xl px-4">
                Game <span className="text-transparent bg-clip-text bg-gradient-to-b from-gray-200 to-gray-600">Meets</span> <br />
                <span className="text-[#A1FF4F] inline-block mt-2">
                    World
                </span>
              </h1>
          </motion.div>
        </div>

        {/* SUBTEXT */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="relative px-8 py-4 mt-4"
        >
            {/* Tech Borders - All 4 Corners for Symmetry */}
            <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-[#A1FF4F]"></div>
            <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-[#A1FF4F]"></div>
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-[#A1FF4F]"></div>
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-[#A1FF4F]"></div>
            
            <p className="max-w-2xl mx-auto text-white/80 font-light tracking-wide text-xl md:text-2xl font-mono uppercase italic">
              {heroSubtext}
            </p>
        </motion.div>

        {/* CTA BUTTONS */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full pt-8"
        >
          {/* Primary Button */}
           <button 
            className="group relative min-w-[200px] px-8 py-4 bg-accent-lime border border-accent-lime overflow-hidden focus:outline-none focus:ring-2 focus:ring-accent-lime focus:ring-offset-2 focus:ring-offset-[#021206]"
            style={{ clipPath: 'polygon(10% 0, 100% 0, 90% 100%, 0% 100%)' }}
            aria-label="Join the community"
           >
             <span className="relative z-10 font-black text-black uppercase tracking-wider flex items-center justify-center gap-2">
               Join the Vibe <MonitorPlay className="w-5 h-5" />
             </span>
           </button>

         {/* Secondary Button */}
<button 
  onClick={scrollToContent} // <--- ADD THIS LINE
  className="group relative min-w-[200px] px-8 py-4 bg-transparent border border-white/20 hover:border-[#A1FF4F] transition-colors focus:outline-none focus:ring-2 focus:ring-[#A1FF4F] focus:ring-offset-2 focus:ring-offset-[#021206]"
  style={{ clipPath: 'polygon(10% 0, 100% 0, 90% 100%, 0% 100%)' }}
  aria-label="Watch live streams"
>
   <div className="absolute inset-0 bg-[#A1FF4F]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
   <span className="relative z-10 font-black text-white group-hover:text-[#A1FF4F] uppercase tracking-wider flex items-center justify-center gap-2">
      Watch Live <Play className="w-4 h-4 fill-current" />
   </span>
</button>
        </motion.div>
      </motion.div>
    </section>
  );
}
